import { IClientBusinessTypeModel } from '../../../../../../../Types/ClientModelTypes';
import { all, any, without, uniq, append } from 'ramda';
import {
  ITypeTreeConditionObject,
  UIUrvalConditionObjectType,
} from '../../../../../../../Types/UrvalTypes';
import {
  getValidClassesList,
  getAllChildTypeValues,
} from '../../../../../../../Model/avenyModelUtils';
import { ITreeSection } from '../../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';

export const isLeafNode = (businessType: IClientBusinessTypeModel): boolean =>
  businessType.ChildTypes.length === 0;

export const getLeafNodes = (
  businessType: IClientBusinessTypeModel
): IClientBusinessTypeModel[] => {
  let leafNodes: IClientBusinessTypeModel[] = [];

  for (let i = 0; i < businessType.ChildTypes.length; i++) {
    const node = businessType.ChildTypes[i];

    if (isLeafNode(node)) {
      leafNodes.push(node);
    } else {
      leafNodes = leafNodes.concat(getLeafNodes(node));
    }
  }

  return leafNodes;
};

export const getTypeValue = (businessType: IClientBusinessTypeModel): string =>
  businessType.TypeValue.toUpperCase();

export const includesAllLeafNodes = (
  conditionValues: string[],
  businessType: IClientBusinessTypeModel
) => {
  const leafNodeTypeValues = getLeafNodes(businessType).map(getTypeValue);
  const conditionValuesIncludes = (typeValue: string) =>
    conditionValues.includes(typeValue);
  return all(conditionValuesIncludes, leafNodeTypeValues);
};

export const includesAnyLeafNode = (
  conditionValues: string[],
  businessType: IClientBusinessTypeModel
) => {
  const leafNodeTypeValues = getLeafNodes(businessType).map(getTypeValue);
  const conditionValuesIncludes = (typeValue: string) =>
    conditionValues.includes(typeValue);
  return any(conditionValuesIncludes, leafNodeTypeValues);
};

export const removeAll = (
  leafNodes: IClientBusinessTypeModel[],
  condition: string[]
): string[] => without(leafNodes.map(getTypeValue), condition);

export const addAll = (
  leafNodes: IClientBusinessTypeModel[],
  condition: string[]
): string[] => uniq(leafNodes.map(getTypeValue).concat(condition));

export const isChecked = (
  businessType: IClientBusinessTypeModel,
  condition: string[]
): boolean => {
  if (isLeafNode(businessType)) {
    return condition.includes(getTypeValue(businessType));
  } else {
    return includesAllLeafNodes(condition, businessType);
  }
};

const getLabels = (
  condition: string[],
  businessType: IClientBusinessTypeModel
): string[] => {
  if (condition.length === 0) {
    return [businessType.POLabel];
  }

  let labels: string[] = [];

  if (isChecked(businessType, condition)) {
    labels.push(businessType.POLabel);
  } else {
    if (!isLeafNode(businessType)) {
      businessType.ChildTypes.forEach(childType => {
        labels = labels.concat(getLabels(condition, childType));
      });
    }
  }

  return labels;
};

export const getTypeLabels = (
  conditionObject: ITypeTreeConditionObject
): string[] => {
  const businessTypes = getBusinessTypeNodes(
    conditionObject.implicitCondition,
    conditionObject.typeTree
  );

  let labels: string[] = [];

  businessTypes.forEach(businessType => {
    labels = uniq(
      labels.concat(getLabels(conditionObject.condition, businessType))
    );
  });

  return labels;
};

/**
 * Checks if a list of component names is valid in a list of type conditons
 */
export const isValidForType = (
  validInComponents: string[],
  typeConditionObject: ITypeTreeConditionObject | undefined
): boolean => {
  if (typeConditionObject == null || validInComponents.length === 0) {
    return true;
  }

  const validComponentsLower = getValidComponents(typeConditionObject).map(v =>
    v.toLowerCase()
  );

  return any(
    validInComponent =>
      validComponentsLower.includes(validInComponent.toLowerCase()),
    validInComponents
  );
};

/**
 * Checks if a condition object is valid for provided typeConditionObject
 */
export const isValidForTypeConditionObject = (
  conditionObject: UIUrvalConditionObjectType,
  typeConditionObject: ITypeTreeConditionObject | undefined
): boolean => {
  if (typeConditionObject == null) {
    return true;
  }

  const validInComponents = getValidInComponents(conditionObject);

  return isValidForType(validInComponents, typeConditionObject);
};

export const getValidInComponents = (
  conditionObject: UIUrvalConditionObjectType
): string[] => {
  let validInComps: string[] = [];
  if (
    conditionObject.modelPath == null ||
    conditionObject.modelPath.length === 0
  ) {
    if (conditionObject.type !== 'Extended') {
      validInComps = conditionObject.attribute.ValidInComps ?? [];
    } else {
      validInComps = conditionObject.association.ValidInComps;
    }
  } else {
    validInComps = conditionObject.modelPath[0].ValidInComps;
  }

  return validInComps;
};

const getBusinessTypeNode = (
  businessTypeNode: IClientBusinessTypeModel,
  componentName: string
): IClientBusinessTypeModel | undefined => {
  if (componentName === businessTypeNode.TypeValue) {
    return businessTypeNode;
  } else if (businessTypeNode.ChildTypes.length > 0) {
    for (let i = 0; i < businessTypeNode.ChildTypes.length; i++) {
      const childType = businessTypeNode.ChildTypes[i];
      const typeNode = getBusinessTypeNode(childType, componentName);
      if (typeNode) {
        return typeNode;
      }
    }
    return undefined;
  } else {
    return undefined;
  }
};

const businessTypeNodeReducer =
  (businessTypeNode: IClientBusinessTypeModel) =>
  (businessTypeNodes: IClientBusinessTypeModel[], componentName: string) => {
    const businessType = getBusinessTypeNode(businessTypeNode, componentName);
    if (businessType) {
      return append(businessType, businessTypeNodes);
    } else {
      return businessTypeNodes;
    }
  };

const businessTypeNodeSectionReducer =
  (conditionObject: ITypeTreeConditionObject) =>
  (
    businessTypeNodes: ITreeSection<ITypeTreeConditionObject>[],
    componentName: string
  ) => {
    const businessType = getBusinessTypeNode(
      conditionObject.typeTree,
      componentName
    );
    if (businessType) {
      return append(
        {
          name: conditionObject.typeTree.SOLabel,
          value: conditionObject.typeTree.TypeValue,
          data: conditionObject,
          id: conditionObject.typeTree.TypeValue,
          subList: buildSubList(
            conditionObject.typeTree.ChildTypes,
            conditionObject.condition
          ),
        },
        businessTypeNodes
      );
    } else {
      return businessTypeNodes;
    }
  };

/** Gets childtypes of businessTypeNode that is contained in  */
export const getBusinessTypeNodes = (
  componentNames: string[],
  businessTypeNode: IClientBusinessTypeModel
): IClientBusinessTypeModel[] => {
  if (componentNames.length === 0) {
    return [businessTypeNode];
  }
  return componentNames.reduce(businessTypeNodeReducer(businessTypeNode), []);
};

const getValidComponents = (
  typeConditionObject: ITypeTreeConditionObject
): string[] => {
  const { condition, implicitCondition, typeTree } = typeConditionObject;

  const businessTypeNodes = getBusinessTypeNodes(implicitCondition, typeTree);

  let validComps: string[] = [];

  condition.forEach(cond => {
    validComps = uniq(
      validComps.concat(getValidClassesList(businessTypeNodes, cond))
    );
  });

  if (validComps.length === 0) {
    validComps = getAllChildTypeValues(businessTypeNodes);
  }

  return validComps;
};

const buildSubList = (
  nodes: IClientBusinessTypeModel[],
  condition: string[]
): ITreeSection<ITypeTreeConditionObject>[] => {
  return nodes
    .filter(x => x.Visible !== false)
    .map(x => ({
      name: x.SOLabel,
      value: x.TypeValue,
      id: x.TypeValue,
      checked: isChecked(x, condition),
      subList: buildSubList(x.ChildTypes, condition),
    }));
};

export const getTreePickerSections = (
  conditionObject: ITypeTreeConditionObject
): ITreeSection<ITypeTreeConditionObject>[] => {
  if (conditionObject.implicitCondition.length === 0) {
    return [
      {
        name: conditionObject.typeTree.SOLabel,
        value: conditionObject.typeTree.TypeValue,
        id: conditionObject.typeTree.TypeValue,
        data: conditionObject,
        open: true,
        subList: buildSubList(
          conditionObject.typeTree.ChildTypes,
          conditionObject.condition
        ),
      },
    ];
  }
  return conditionObject.implicitCondition.reduce(
    businessTypeNodeSectionReducer(conditionObject),
    []
  );
};

const typeTreeToSection = (
  trees: IClientBusinessTypeModel[]
): ITreeSection<IClientBusinessTypeModel>[] => {
  return trees.map(x => ({
    name: x.SOLabel,
    value: x.TypeValue,
    id: x.TypeValue,
    data: x,
    subList: typeTreeToSection(x.ChildTypes),
  }));
};
